// components/ShareButtons.tsx
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  FacebookShareButton, 
  TwitterShareButton, 
  WhatsappShareButton,
  FacebookIcon,
  XIcon,
  WhatsappIcon 
} from 'react-share';
import { CiShare2 } from "react-icons/ci";

interface ShareButtonsProps {
  prospect?: any;
  photoURL?: string | null;
  isHE: boolean;
  title?: string;       // override for non-prospect pages
  description?: string; // override for non-prospect pages
  fixedFloat?: boolean; // pin the mobile button to a viewport corner
  stickyTop?: boolean;  // place the mobile button near the top of the article (absolute, not fixed/sticky) so it scrolls away naturally with the content
  accentColor?: string; // override the trigger button's background color (defaults to site orange)
}

const ShareButtons: React.FC<ShareButtonsProps> = ({ prospect, photoURL, isHE, title, description, fixedFloat, stickyTop, accentColor = "#F88158" }) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const shareUrl = window.location.href;
  const shareTitle =
    title ?? `${prospect.full_name[0]} - ${prospect.pos_and_roles[0].position} | Hoops Boarding Gate`;
  const shareDescription =
    description ?? ((isHE ? prospect.summary[1] : prospect.summary[0])?.substring(0, 150) + '...');

  return (
    <>
      {/* Meta tags for social media preview */}
      <Helmet>
        <title>{shareTitle}</title>
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:image" content={photoURL || "https://your-site.com/default-preview.jpg"} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={shareTitle} />
        <meta name="twitter:description" content={shareDescription} />
        <meta name="twitter:image" content={photoURL || "https://your-site.com/default-preview.jpg"} />
      </Helmet>

      {/* Desktop Share Buttons */}
      <div className="hidden md:flex justify-end mb-4 gap-3">
        <FacebookShareButton
          url={shareUrl}
          title={shareDescription}
          hashtag="#NBADraft"
          className="rounded-full transition-transform hover:scale-110 active:scale-95"
        >
          <span className="block rounded-full shadow-elevation ring-1 ring-white/20">
            <FacebookIcon size={32} round />
          </span>
        </FacebookShareButton>

        <TwitterShareButton
          url={shareUrl}
          title={shareTitle}
          hashtags={['NBADraft', 'HoopsBG']}
          className="rounded-full transition-transform hover:scale-110 active:scale-95"
        >
          <span className="block rounded-full shadow-elevation ring-1 ring-white/20">
            <XIcon size={32} round />
          </span>
        </TwitterShareButton>

        <WhatsappShareButton
          url={shareUrl}
          title={shareTitle}
          separator=" - "
          className="rounded-full transition-transform hover:scale-110 active:scale-95"
        >
          <span className="block rounded-full shadow-elevation ring-1 ring-white/20">
            <WhatsappIcon size={32} round />
          </span>
        </WhatsappShareButton>
      </div>

      {/* Mobile Floating Share Button */}
      <div className={
        stickyTop
          ? "absolute top-24 right-1 z-40 mb-3 w-fit md:hidden" // sits near the top of the article, scrolls away normally with the content
          : fixedFloat
          ? (isHE
              ? "fixed bottom-4 right-4 z-50 md:hidden"  // Pinned to avatar side (RTL = right)
              : "fixed bottom-4 left-4 z-50 md:hidden")  // Pinned to avatar side (LTR = left)
          : (isHE
              ? "absolute bottom-100 left-1 z-50 md:hidden"  // Left side for Hebrew
              : "absolute bottom-100 right-1 z-50 md:hidden") // Right side for English
      }>
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="p-3 text-white rounded-full shadow-elevation-lg hover:scale-105 active:scale-95 transition"
          style={{ backgroundColor: accentColor }}
          aria-label={isHE ? "שתף" : "Share"}
        >
          <CiShare2 size={20} />
        </button>
        
        {showMobileMenu && (
          <div className={
            stickyTop
              ? "absolute top-16 left-0 flex flex-col gap-2 animate-fade-in" // opens downward, below the sticky trigger
              : fixedFloat
              ? (isHE
                  ? "absolute bottom-16 right-0 flex flex-col gap-2 animate-fade-in"
                  : "absolute bottom-16 left-0 flex flex-col gap-2 animate-fade-in")
              : (isHE
                  ? "absolute bottom-16 left-0 flex flex-col gap-2 animate-fade-in"   // Opens to the right of button
                  : "absolute bottom-16 right-0 flex flex-col gap-2 animate-fade-in") // Opens to the left of button
          }>
            <FacebookShareButton url={shareUrl} title={shareDescription} hashtag="#NBADraft" className="rounded-full transition-transform hover:scale-110 active:scale-95">
              <span className="block rounded-full shadow-elevation-lg ring-1 ring-white/20">
                <FacebookIcon size={40} round />
              </span>
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareTitle} hashtags={['NBADraft', 'HoopsBG']} className="rounded-full transition-transform hover:scale-110 active:scale-95">
              <span className="block rounded-full shadow-elevation-lg ring-1 ring-white/20">
                <XIcon size={40} round />
              </span>
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareTitle} className="rounded-full transition-transform hover:scale-110 active:scale-95">
              <span className="block rounded-full shadow-elevation-lg ring-1 ring-white/20">
                <WhatsappIcon size={40} round />
              </span>
            </WhatsappShareButton>
          </div>
        )}
      </div>
    </>
  );
};

export default ShareButtons;